import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/common";
import { money, fmtDate } from "@/lib/format";
import { owedFigure } from "@/lib/advanceOwed";
import { OwedFigures, useAdvanceTotals } from "@/components/AdvanceTotalsBanner";
import { toast } from "sonner";
import { HandCoins, Gift, ArrowRight, CheckCircle, ClockCounterClockwise, Trash, CaretDown, Warning } from "@phosphor-icons/react";
import { RecordRepaymentForm, RepaymentLine } from "@/components/RecordRepaymentForm";
import { MoneyEnvelope } from "@/components/icons";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { HomeCurrencyEstimate, useHomeEstimate } from "@/components/HomeCurrencyEstimate";
import { curCode } from "@/lib/currencies";

// MONEY ENVELOPES (Maryse, 26 Oct 2026, ZEN-814 point 5): the box takes the
// menu's words — "Money Envelopes", then Contributions, Cash Advances, Gifts —
// and the three cards are built the same way: title with icon, ONE short grey
// line, the figures, and the same "Manage … →" link at the bottom. Adding a
// gift happens on the Gifts page, as adding does for the other two. The
// Family Money Tools PDF moved to the band's header.
function ContributionsCard({ gifts, studentId }) {
  const navg = useNavigate();
  const { data: envelopes = [] } = useQuery({
    queryKey: ["envelopes", studentId],
    queryFn: async () => (await api.get(`/envelopes?student_id=${studentId}`)).data,
  });
  const { data: advances = [] } = useQuery({
    queryKey: ["advances", studentId],
    queryFn: async () => (await api.get(`/advances?student_id=${studentId}`)).data,
  });
  const owed = useOwedRow(studentId);
  return (
    <Card data-testid="contributions-card">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-[#1f2937]">Money Envelopes</h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <EnvelopesSub envelopes={envelopes} onManage={() => navg("/contributions")} />
        <AdvancesSub advances={advances} owed={owed} />
        <GiftsSub gifts={gifts} onManage={() => navg("/gifts")} />
      </div>
    </Card>
  );
}

/** One Money Envelopes card: the same shape for all three. */
function EnvelopeCard({ icon, title, line, testid, children, manage }) {
  return (
    <div className="flex flex-col rounded-lg border border-[#C2C9D1] bg-[#F8FAFC] p-3" data-testid={testid}>
      <div className="mb-2">
        <div className="flex items-center gap-2 text-sm font-bold">{icon} {title}</div>
        <div className="mt-0.5 text-[11px] text-[#6b7280]" data-testid={`${testid}-line`}>{line}</div>
      </div>
      <div className="flex-1 space-y-1.5">{children}</div>
      {manage}
    </div>
  );
}

const manageLink = "mt-3 inline-flex items-center gap-1 self-start text-xs font-semibold text-[#034AA2] hover:underline";

function EnvelopesSub({ envelopes, onManage }) {
  const violations = envelopes.reduce((s, e) => s + (e.violations || 0), 0);
  const totalEarmarked = envelopes.reduce((s, e) => s + (e.usd_amount || 0), 0);
  const { data: homeEstEnv } = useHomeEstimate(totalEarmarked);
  const byCur = Object.entries(envelopes.reduce((m, e) => {
    const c = curCode(e.currency, "USD"); const d = m[c] || { ear: 0, rem: 0 };
    d.ear += e.usd_amount || 0; d.rem += e.remaining_usd || 0; m[c] = d; return m;
  }, {})).sort((a, b) => (a[0] === "USD" ? -1 : b[0] === "USD" ? 1 : a[0].localeCompare(b[0])));
  return (
    <EnvelopeCard testid="contrib-envelopes-sub" title="Contributions" line="Draws down as you tag expenses"
      icon={<MoneyEnvelope size={16} className="text-[#034AA2]" />}
      manage={<button onClick={onManage} className={manageLink} data-testid="contrib-envelopes-manage">Manage contributions <ArrowRight size={12} weight="bold" /></button>}>
      {envelopes.length === 0 ? (
        <div className="text-xs text-[#6b7280]">No envelopes yet. Earmark money that draws down as you tag expenses.</div>
      ) : (
        <>
          {byCur.map(([cur, v]) => (
            <div key={cur} className="flex items-center justify-between rounded-md bg-white px-2.5 py-1.5 text-xs">
              <span className="font-bold text-[#077A39]">{money(v.rem, cur)} left</span>
              <span className="text-[#6b7280]">of {money(v.ear, cur)}</span>
            </div>
          ))}
          {violations > 0 && (
            <div className="flex items-center gap-1 rounded-md bg-[#FBE9E9] px-2 py-1 text-[11px] font-semibold text-[#9F1010]"><Warning size={12} weight="bold" /> {violations} eligibility issue{violations === 1 ? "" : "s"}</div>
          )}
          <HomeCurrencyEstimate estimate={homeEstEnv?.estimate} />
          <div className="text-[11px] text-[#6b7280]">{envelopes.length} envelope{envelopes.length === 1 ? "" : "s"}</div>
        </>
      )}
    </EnvelopeCard>
  );
}

function GiftsSub({ gifts, onManage }) {
  return (
    <EnvelopeCard testid="contrib-gifts-sub" title="Gifts" line="For your records only"
      icon={<Gift size={16} weight="bold" className="text-[#077A39]" />}
      manage={<button onClick={onManage} className={manageLink} data-testid="contrib-gifts-manage">Manage gifts <ArrowRight size={12} weight="bold" /></button>}>
      {gifts?.total > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-md bg-[#077A39] px-2.5 py-1 text-xs font-bold text-white">Total {money(gifts.total, "USD")}</span>
          {gifts.by_giver.map((g) => <span key={g.nickname} className="rounded-md bg-[#E9E9EB] px-2.5 py-1 text-xs"><b>{g.nickname}</b> · {money(g.total, "USD")}</span>)}
        </div>
      ) : <div className="text-xs text-[#6b7280]">No gifts logged.</div>}
    </EnvelopeCard>
  );
}

// What this child still owes, per currency, worked out by the server: the same
// figures as the Cash Advances page, never added across currencies. (ZEN-779)
function useOwedRow(studentId) {
  const { data: totals } = useAdvanceTotals();
  return totals?.beneficiaries?.find((b) => b.student_id === studentId) || null;
}

function AdvancesSub({ advances, owed }) {
  const outstanding = advances.filter((a) => a.remaining > 0.005);
  // Amber only while money is owed: once every advance is repaid the title's
  // icon goes grey. (Maryse, ZEN-810)
  const allRepaid = advances.length > 0 && outstanding.length === 0;
  return (
    <EnvelopeCard testid="contrib-advances-sub" title="Cash Advances" line="Paid back over time"
      icon={<HandCoins size={16} weight="bold" className={allRepaid ? "text-[#6b7280]" : "text-[#D97706]"} data-testid="contrib-advances-icon" />}
      manage={<a href="/advances" className={manageLink} data-testid="contrib-advances-manage">Manage cash advances <ArrowRight size={12} weight="bold" /></a>}>
        {outstanding.length > 0 ? (
          <>
            <div className="flex items-start justify-between gap-2 rounded-md bg-white px-2.5 py-1.5 text-xs" data-testid="contrib-advances-owed">
              <div className="min-w-0">{owed && <OwedFigures row={owed} className="font-bold text-[#D97706]" />}</div>
              <span className="shrink-0 text-[#6b7280]">{outstanding.length} advance{outstanding.length === 1 ? "" : "s"}</span>
            </div>
            {outstanding.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-md bg-white px-2.5 py-1.5 text-xs" data-testid={`advance-summary-${a.id}`}>
                <span className="font-semibold">{a.from_nickname || "—"}</span>
                <span className="tnum text-[#D97706]">{owedFigure(a)}</span>
              </div>
            ))}
          </>
        ) : advances.length > 0 ? (
          <div className="text-xs text-[#077A39] font-semibold">All advances repaid</div>
        ) : (
          <div className="text-xs text-[#6b7280]">No cash advances yet. Record money fronted to the beneficiary.</div>
        )}
    </EnvelopeCard>
  );
}

function AdvancesRollup({ studentId }) {
  const qc = useQueryClient();
  const { data: advances = [] } = useQuery({
    queryKey: ["advances", studentId],
    queryFn: async () => (await api.get(`/advances?student_id=${studentId}`)).data,
    enabled: !!studentId,
  });
  const outstanding = advances.filter((a) => a.remaining > 0.005);
  const owed = useOwedRow(studentId);
  const [open, setOpen] = useState(true);
  const [repayIdx, setRepayIdx] = useState(null);
  const [histIdx, setHistIdx] = useState(null);
  const [undoTarget, setUndoTarget] = useState(null); // { advanceId, repayId }

  if (!outstanding.length) return null;

  const undoRepay = async (a, rid) => {
    try {
      await api.delete(`/advances/${a.id}/repay/${rid}`);
      toast.success("Repayment removed");
      setUndoTarget(null);
      qc.invalidateQueries({ queryKey: ["advances"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (<>
    <Card data-testid="advances-rollup" className="border-[#D97706]/40">
      <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-3" data-testid="advances-rollup-toggle">
        <div className="flex min-w-[14rem] flex-1 items-center gap-2.5">
          <HandCoins size={20} weight="bold" className="shrink-0 text-[#D97706]" />
          <div className="text-left">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#1f2937]">Cash advances — awaiting repayment</h3>
            <p className="text-xs text-[#6b7280]">Fronted money the beneficiary owes back · {outstanding.length} advance{outstanding.length === 1 ? "" : "s"}</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-right" data-testid="advances-rollup-owed">{owed && <OwedFigures row={owed} className="tnum text-xl font-extrabold text-[#D97706]" />}</div>
          <CaretDown size={16} weight="bold" className={`text-[#6b7280] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="mt-3 divide-y divide-[#C2C9D1] border-t border-[#C2C9D1] pt-1">
          {outstanding.map((a, i) => (
            <div key={a.id} className="py-2" data-testid={`advance-item-${a.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold">{a.from_nickname || "—"} <ArrowRight size={12} weight="bold" className="inline -mt-0.5 text-[#6b7280]" /> Beneficiary</div>
                  <div className="text-xs text-[#6b7280]">{fmtDate(a.date)} · {money(a.amount, a.currency)} fronted{a.note ? ` · ${a.note}` : ""}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="tnum font-bold text-[#D97706]" data-testid={`advance-owed-${a.id}`}>{owedFigure(a)}</div>
                  {repayIdx !== i && (
                    <div className="flex flex-wrap items-center gap-2">
                      <button onClick={() => setRepayIdx(i)} data-testid={`advance-repay-btn-${a.id}`}
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-[#077A39] bg-[#077A39]/5 px-2.5 text-xs font-semibold text-[#077A39] hover:bg-[#077A39]/10">
                        <CheckCircle size={14} weight="bold" /> Record repayment
                      </button>
                      <span className="text-[11px] text-[#6b7280]">Beneficiary → {a.from_nickname || "—"}</span>
                      {(a.repayments || []).length > 0 && (
                        <button onClick={() => setHistIdx(histIdx === i ? null : i)} data-testid={`advance-history-btn-${a.id}`}
                          className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs font-semibold text-[#034AA2] hover:underline">
                          <ClockCounterClockwise size={14} weight="bold" /> History
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {histIdx === i && (
                <div className="mt-2 rounded-md border border-[#C2C9D1] bg-[#F8FAFC] p-2.5">
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#6b7280]">Repayment history</div>
                  {(a.repayments || []).length === 0 ? <div className="text-xs text-[#6b7280]">No repayments yet.</div> : (
                    <div className="divide-y divide-[#E9E9EB]">
                      {(a.repayments || []).map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2 py-1.5 text-xs">
                          <RepaymentLine r={r} a={a} />
                          <button onClick={() => setUndoTarget({ advanceId: a.id, repayId: r.id })} className="rounded p-1 text-[#F01919] hover:bg-[#E9E9EB]" aria-label="delete"><Trash size={13} weight="bold" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {repayIdx === i && <RecordRepaymentForm a={a} onDone={() => setRepayIdx(null)} />}
            </div>
          ))}
        </div>
      )}
    </Card>
    <DeleteConfirmDialog open={!!undoTarget} onClose={() => setUndoTarget(null)}
      onConfirm={() => { const a = outstanding.find((x) => x.id === undoTarget?.advanceId); if (a) undoRepay(a, undoTarget.repayId); }}
      title="Delete this repayment?" description="The repayment amount will be restored to the outstanding balance." />
  </>);
}

export { ContributionsCard, AdvancesRollup, AdvancesSub };
